import mixpanel from 'mixpanel-browser';
import { IS_BETA } from '../envVars';

export const STEP_NAMES = Object.freeze({
  ELIGIBILITY: 'eligibility',
  COMPANY_INFO: 'company_info',
  LEGAL_REP: 'legal_rep',
  OWNERS: 'owners',
  UBO: 'ubo',
  KYC_SUBMITTED: 'kyc_submitted',
  VPOS: 'vpos',
  VIDEO_ID: 'video_id',
  CONTRACT_USER: 'contract_user_signed',
  CONTRACT_COMPANY: 'contract_company_signed',
  ONBORDING_COMPLETED: 'onboarding_completed',
});

const KYC_STARTED_LOCAL_KEY_PREFIX = 'mp_kyc_started_';
const STEP_VIEWED_SESSION_KEY_PREFIX = 'mp_step_viewed_';
const ATTEMPT_KEY_PREFIX = 'mp_attempt_';
const VIEWED_AT_KEY_PREFIX = 'mp_viewed_at_';

const suffixForPerson = (personID) =>
  personID !== undefined && personID !== null && personID !== ''
    ? `_${personID}`
    : '';

const attemptKey = (stepName, personID) =>
  `${ATTEMPT_KEY_PREFIX}${stepName}${suffixForPerson(personID)}`;

const viewedAtKey = (stepName, personID) =>
  `${VIEWED_AT_KEY_PREFIX}${stepName}${suffixForPerson(personID)}`;

const stepViewedKey = (stepName, personID) =>
  `${STEP_VIEWED_SESSION_KEY_PREFIX}${stepName}${suffixForPerson(personID)}`;

const kycStartedKey = (userId) =>
  `${KYC_STARTED_LOCAL_KEY_PREFIX}${userId || 'anon'}`;

export const resolveCompanyStatus = (userInfo) =>
  !(userInfo?.legal && userInfo?.posId > 0) ? 'not_approved' : 'approved';

// Mirrors STATUS_KYC in
// app/components/SharedConstantsAndMessages/constants.js. Converts the numeric
// status returned by /kyc/status into a readable label for analytics.
export const kycStatusToString = (statusCode) => {
  switch (statusCode) {
    case 0x00:
      return 'new';
    case 0x01:
      return 'pending';
    case 0x02:
      return 'declined';
    case 0x03:
      return 'need_attention';
    case 0x04:
      return 'need_review';
    case 0x05:
      return 'approved';
    case 0x06:
      return 'incomplete';
    default:
      return undefined;
  }
};

export const initializeMixpanel = () => {
  const token = process.env.APP_MIXPANEL_TOKEN;
  if (!token || process.env.NODE_ENV === 'development' || IS_BETA) {
    // eslint-disable-next-line no-console
    console.error(`Mixpanel token is missing! NODE: ${process.env.NODE_ENV}`);
    return;
  }

  mixpanel.init(token, {
    autocapture: false,
    record_sessions_percent: 100,
    api_host: 'https://api-eu.mixpanel.com',
    persistence: 'localStorage',
    before_send: (event) => {
      if (event?.event === '$mp_session_recording_check') return null;
      return event;
    },
  });
};

// eslint-disable-next-line no-underscore-dangle
const isReady = () => mixpanel && mixpanel.__loaded === true;

const getAttemptNumber = (stepName, personID) => {
  try {
    const raw = sessionStorage.getItem(attemptKey(stepName, personID));
    const parsed = parseInt(raw, 10);
    return Number.isNaN(parsed) ? 0 : parsed;
  } catch {
    return 0;
  }
};

const incrementAttempt = (stepName, personID) => {
  const next = getAttemptNumber(stepName, personID) + 1;
  try {
    sessionStorage.setItem(attemptKey(stepName, personID), String(next));
  } catch {
    // sessionStorage unavailable — fall through, event still fires with next value
  }
  return next;
};

const markViewedAt = (stepName, personID) => {
  try {
    sessionStorage.setItem(viewedAtKey(stepName, personID), String(Date.now()));
  } catch {
    // sessionStorage unavailable — time_spent_seconds will be omitted
  }
};

const readTimeSpentSeconds = (stepName, personID) => {
  try {
    const raw = sessionStorage.getItem(viewedAtKey(stepName, personID));
    const viewedAt = parseInt(raw, 10);
    if (Number.isNaN(viewedAt)) return undefined;
    const delta = Date.now() - viewedAt;
    if (delta < 0) return undefined;
    return Math.round(delta / 1000);
  } catch {
    return undefined;
  }
};

export const formatRejection = (error) => {
  if (!error) return undefined;
  const details = Array.isArray(error.details) ? error.details : [];
  if (details.length === 0) {
    return error.code ? `${error.code}\n${error.message || ''}` : undefined;
  }
  const parts = details
    .map((d) => {
      if (!d) return null;
      const field = d.field || '';
      const code = d.code || '';
      if (field && code) return `${field}: ${code}`;
      return field || code || null;
    })
    .filter(Boolean);
  if (parts.length === 0) return undefined;
  const joined = parts.join('; ');
  return joined.length > 200 ? `${joined.slice(0, 197)}...` : joined;
};

const resolveStatus = (status) => {
  if (status === undefined || status === null) return undefined;
  if (typeof status === 'number') return kycStatusToString(status);
  return status;
};

export const identifyUser = (userId) => {
  if (!isReady()) return;
  mixpanel.identify(String(userId));
};

export const trackKYCStarted = ({ status, companyStatus, userId } = {}) => {
  if (!isReady()) return;
  const resolved = resolveStatus(status);
  // Only emit on a fresh KYC. Once status has progressed past 'new', the user
  // has already started — don't re-emit on later visits.
  if (resolved !== undefined && resolved !== 'new') return;
  try {
    const key = kycStartedKey(userId);
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, '1');
  } catch {
    // localStorage unavailable — fall through and still emit
  }
  const props = {};
  if (resolved !== undefined) props.status = resolved;
  if (companyStatus !== undefined) props.company_status = companyStatus;
  mixpanel.track('KYC Started', props);
};

export const trackStepViewed = (
  stepName,
  { status, personID, companyStatus } = {}
) => {
  if (!isReady()) return;
  try {
    const key = stepViewedKey(stepName, personID);
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');
  } catch {
    // sessionStorage unavailable — fall through and still emit
  }
  markViewedAt(stepName, personID);
  const props = {
    step_name: stepName,
    attempt_number: getAttemptNumber(stepName, personID),
    company_status: companyStatus,
  };
  const resolved = resolveStatus(status);
  if (resolved !== undefined) props.status = resolved;
  mixpanel.track('KYC Step Viewed', props);
};

export const trackStepSubmitted = (stepName, { status, personID } = {}) => {
  if (!isReady()) return;
  const attemptNumber = incrementAttempt(stepName, personID);
  const timeSpentSeconds = readTimeSpentSeconds(stepName, personID);
  const props = {
    step_name: stepName,
    attempt_number: attemptNumber,
  };
  const resolved = resolveStatus(status);
  if (resolved !== undefined) props.status = resolved;
  if (typeof timeSpentSeconds === 'number') {
    props.time_spent_seconds = timeSpentSeconds;
  }
  mixpanel.track('KYC Step Submitted', props);
};

export const trackStepValidated = (
  stepName,
  { status, rejectionReason, personID } = {}
) => {
  if (!isReady()) return;
  const props = {
    step_name: stepName,
    attempt_number: getAttemptNumber(stepName, personID),
    rejection_reason: rejectionReason,
  };
  const resolved = resolveStatus(status);
  if (resolved !== undefined) props.status = resolved;
  if (rejectionReason) {
    props.rejection_reason = rejectionReason;
  }
  mixpanel.track('KYC Step Validated', props);
};

const PRAGMA_FORM_SUBMITTED_KEY = 'mp_prg_sub';

export const trackPragmaFormSubmitted = () => {
  if (!isReady()) return;
  try {
    if (localStorage.getItem(PRAGMA_FORM_SUBMITTED_KEY)) return;
    localStorage.setItem(PRAGMA_FORM_SUBMITTED_KEY, '1');
  } catch {
    // localStorage unavailable — fall through and still emit
  }
  mixpanel.track('Pragma Form Submitted');
};

export const trackKYCSentForApproval = ({ rejectionReason } = {}) => {
  if (!isReady()) return;
  const props = {
    rejection_reason: rejectionReason,
  };
  if (rejectionReason) {
    props.rejection_reason = rejectionReason;
  }
  mixpanel.track('KYC Sent For Approval', props);
};
